import {
  Dropdown,
  Menu,
  message,
  Table,
  Tag,
  Row,
  Input,
  Select,
  Button,
  Modal,
  Typography,
  Form,
  Col,
  InputNumber,
} from "antd";
import { MoreOutlined } from "@ant-design/icons";

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "aws-amplify";
import { listEpcEmployersByCreatedAt } from "graphql/customEpcQuery";
import { callGraphQLBatch } from "utils/api";
import TableSkeleton from "utility/TableSkeleton";
import search from "utility/search";
import { Option } from "antd/lib/mentions";
import CommonTable from "../commonComponents/Table";
import CommonCSVExport from "../commonComponents/CSVExport";
import CommonSearch from "../commonComponents/Search";
import {
  doctorNameRegex,
  emailPattern,
  formatNumberUS,
  tenDigit,
  urlPattern,
} from "utility/constant";

interface UserData {
  key: string;
  email: string;
  name: string;
  phone?: string;
  Action: string;
  createdAt: string;
  status: string;
}

interface FormData {
  name: string;
  email: string;
  website: string;
  phone: string;
}

export default function EmployersListing() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<UserData[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();
  const columns = [
    {
      title: "Employer Name",
      dataIndex: "name",
      width: 220,
    },
    {
      title: "Employer Email",
      dataIndex: "email",
      width: 180,
    },
    {
      title: "Employer Phone",
      dataIndex: "phone",
      width: 180,
    },
    {
      title: "Billing Status",
      dataIndex: "status",
      width: 180,
      render: (status: string) => (
        <span
          style={{
            color: status === "ACTIVE" ? "#52c41a" : "#faad14",
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {status === "CREATED" ? "Not Started" : status}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "Action",
      className: "actions",
      width: 50,
      render: (id: String) => {
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key={1}>
                  <Link
                    to={`/dpc/employer-profile/${id}`}
                    state={{ tab: "3", id: id }}
                  >
                    Edit
                  </Link>
                </Menu.Item>
              </Menu>
            }
            overlayClassName="download-btn"
            placement="bottomRight"
          >
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const fetchAllEmployers = useCallback(async () => {
    try {
      setLoading(true);
      const variables = {
        sortDirection: "DESC",
      };
      const [employerList] = await callGraphQLBatch([
        {
          query: listEpcEmployersByCreatedAt,
          authMode: "AMAZON_COGNITO_USER_POOLS",
          variables,
          outputKeyword: "employerByCreatedAt",
        },
      ]);

      // Merge and map data
      const merged = [...(employerList || [])].map((item) => ({
        key: item.id,
        email: item.email,
        name: item.profile.name || "-",
        phone: item.profile.phone ? formatNumberUS(item.profile.phone) : "-",
        status: item.DpcDetails.accountStatus || "-",
        Action: item.id,
        createdAt: item.createdAt,
      }));
      setDataSource(merged);
      setUserData(merged);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllEmployers();
  }, [fetchAllEmployers]);

  const headers = [
    { label: "Employer Email", key: "email" },
    { label: "Employer Name", key: "name" },
    { label: "Employer Phone", key: "phone" },
    { label: "Billing Status", key: "status" },
  ];

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const currValue = e.target.value.trim();
    setSearchValue(e.target.value);
    const searchFields = ["email", "doctorName", "name", "role"];
    const filteredData = search(userData, currValue, searchFields);
    setDataSource(filteredData);
  };

  const addEmployer = async (values: FormData) => {
    setCreateLoading(true);
    try {
      const payload = {
        ...values,
        phone: values.phone ? String(values.phone) : undefined,
      };
      const myInit = {
        body: payload,
      };
      const response = await API.post(
        "bazarhealthREST",
        "/epc/superadmin/addEmployer",
        myInit
      );
      messageApi.open({
        type: "success",
        content: response.message || "Employer created successfully!",
      });
      fetchAllEmployers();
    } catch (error: any) {
      messageApi.open({
        type: "error",
        content:
          error.response.data.message ||
          "Failed to create employer. Please try again.",
      });
    } finally {
      setIsModalOpen(false);
      form.resetFields();
      setCreateLoading(false);
    }
  };

  function getCsvExportData() {
    return dataSource.map((row) => {
      const { email, name = "-", phone, status } = row || {};

      return {
        email,
        name,
        phone,
        status,
      };
    });
  }

  return (
    <>
      {contextHolder}
      {loading ? (
        <TableSkeleton />
      ) : (
        <>
          <Row justify="space-between" className="table-header">
            <Row align="middle">
              <CommonSearch onChange={handleSearch} value={searchValue} />
            </Row>
            <Row className="gap-10 mtr-20 bold-font filter-dropdown-list">
              <CommonCSVExport
                data={getCsvExportData()}
                headers={headers}
                filename={`All Employers.csv`}
                onClick={() =>
                  messageApi.open({
                    type: "loading",
                    content: "Exporting CSV...",
                    duration: 1,
                  })
                }
              />

              <Button
                className="blue-btn inherit-btn"
                onClick={() => setIsModalOpen(true)}
              >
                Add Employer
              </Button>
            </Row>{" "}
          </Row>
          <CommonTable
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            className="common-table"
            scroll={{ x: 992 }}
            pagination={{
              position: ["bottomCenter"],
              defaultPageSize: 100,
            }}
          />
        </>
      )}
      <Modal
        className="form-modal"
        centered
        footer={null}
        width={700}
        title={
          <Typography.Title level={3} className="modal-title">
            Add Employer
          </Typography.Title>
        }
        visible={isModalOpen}
        onOk={() => setIsModalOpen(false)}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
      >
        <Form
          name="basic"
          onFinish={addEmployer}
          autoComplete="off"
          layout="vertical"
          form={form}
        >
          <Row gutter={20}>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Employer name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter Employer Name",
                  },
                  {
                    pattern: doctorNameRegex,
                    message: "Please enter a valid Employer name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Employer email id"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter employer email id",
                  },
                  {
                    pattern: emailPattern,
                    message: "Please provide valid Email!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Employer url"
                name="website"
                rules={[
                  {
                    required: true,
                    message: "Please enter employer wesbite url",
                  },
                  {
                    pattern: urlPattern,
                    message: "Please input valid url",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Employer phone number"
                name="phone"
                rules={[
                  {
                    required: true,
                    message: "Please enter employer phone number",
                  },
                  {
                    pattern: tenDigit,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <InputNumber
                  controls={false}
                  formatter={(value) => formatNumberUS(value)}
                  parser={(value) => {
                    const cleanedValue = (value || "").replace(/\D/g, "");
                    return cleanedValue.slice(0, 10);
                  }}
                  style={{ width: "100%", borderRadius: "5px" }}
                />
              </Form.Item>
            </Col>

            <Col
              span={12}
              md={24}
              xs={24}
              sm={24}
              style={{ textAlign: "center" }}
            >
              <Button
                className="theme-btn"
                htmlType="submit"
                loading={createLoading}
              >
                Add Employer
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
